/**
 * The fixed-timestep accumulator AD-007a requires: the sim always advances in
 * whole `FIXED_DT` ticks regardless of how long a rendered frame took, and the
 * renderer interpolates between the last two sim states rather than stepping
 * physics by a variable frame time.
 *
 * Pure arithmetic, deliberately separate from anything that owns a `SimState`
 * or a clock, so the awkward cases (a hidden tab producing a multi-second
 * frame, a zero-length frame, a negative one from a bad clock) are ordinary
 * unit tests rather than things you find out about in flight.
 */

import { FIXED_DT } from "./units.ts";

/**
 * The spiral-of-death guard: at most this many sim ticks run for one rendered
 * frame. Past it, simulated time is *dropped* rather than queued — catching up
 * on a two-second stall would cost more than the frame that caused it and dig
 * the hole deeper. 8 ticks is ~133 ms of sim per frame, comfortably more than
 * a hitching frame needs and comfortably less than a stall's backlog.
 */
export const MAX_CATCHUP_STEPS = 8;

export interface StepPlan {
  /** How many `dt`-long sim ticks to run for this frame. */
  readonly steps: number;
  /** Left-over time carried into the next frame; always in `[0, dt)`. */
  readonly accumulator: number;
  /** True when simulated time was dropped to stay inside `maxSteps`. */
  readonly clamped: boolean;
}

/**
 * Plans one frame's worth of ticks. `accumulator` is the previous plan's
 * leftover; `frameSeconds` is the wall-clock time since the last frame.
 * Non-finite or negative frame times contribute nothing, so a bad clock
 * reading stalls the sim for a frame instead of corrupting it.
 */
export function planFixedSteps(
  accumulator: number,
  frameSeconds: number,
  dt: number = FIXED_DT,
  maxSteps: number = MAX_CATCHUP_STEPS,
): StepPlan {
  const budget = dt * maxSteps;
  const elapsed = Number.isFinite(frameSeconds) ? Math.max(frameSeconds, 0) : 0;
  const bounded = Math.min(elapsed, budget);
  const pending = Math.max(accumulator, 0) + bounded;
  const steps = Math.min(Math.floor(pending / dt), maxSteps);
  return { steps, accumulator: pending - steps * dt, clamped: elapsed > budget };
}

/** Where the render frame sits between the last two sim states, in `[0, 1]`. */
export function interpolationAlpha(accumulator: number, dt: number = FIXED_DT): number {
  if (!Number.isFinite(accumulator) || accumulator <= 0) return 0;
  return Math.min(accumulator / dt, 1);
}
